import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

import { Modal, Button, IconButton, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Box } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';

// import api from '@utils/axiosInstance';
import ProgressWithLabel from './ProgressWithLabel';
import '@resources/assets/styles/_header.scss';

// Utility function to format file size
const formatFileSize = (size) => {
    if (size >= 1024 * 1024 * 1024) {
        return `${(size / (1024 * 1024 * 1024)).toFixed(2)} GB`;
    } else if (size >= 1024 * 1024) {
        return `${(size / (1024 * 1024)).toFixed(2)} MB`;
    } else if (size >= 1024) {
        return `${(size / 1024).toFixed(2)} KB`;
    } else {
        return `${size} Bytes`;
    }
}

function VodUploadModal({ modalOpen, setModalOpen, url, idx }) {
    const [files, setFiles] = useState([]);
    const [videoUrls, setVideoUrls] = useState([]);
    const [progress, setProgress] = useState([]);
    const [status, setStatus] = useState(null);
    const [uploadedFiles, setUploadedFiles] = useState([]);

    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const { t } = useTranslation();

    useEffect(() => {
        if (modalOpen) {
            setProgress([{
                id: 0,
                percentage: 0,
            },
            {
                id: 1,
                percentage: 0,
            },
            {
                id: 2,
                percentage: 0,
            },
            {
                id: 3,
                percentage: 0,
            },
            {
                id: 4,
                percentage: 0,
            }])
        } else {
            setFiles([]);
            setUploadedFiles([]);
            setProgress([]);
        }
    }, [modalOpen])

    const onDrop = useCallback((acceptedFiles) => {
        setFiles(acceptedFiles);
        // const newFiles = acceptedFiles.map((file, idx) => {
        //     const videoUrl = URL.createObjectURL(file instanceof File ? file : new Blob([file]));

        //     return {
        //         id: idx,
        //         name: file.path,
        //         size: file.size,
        //         blob: file,
        //         videoUrl: videoUrl
        //     };
        // });
        // setUploadedFiles(prev => [...prev, ...newFiles]);
        setVideoUrls(prev => [...prev, ...acceptedFiles.map(file => URL.createObjectURL(file instanceof File ? file : new Blob([file])))]);
    }, [])

    useEffect(() => {
        console.log(progress)
    }, [progress])

    const onUpload = () => {
        files.map((file, idx) => {
            const formData = new FormData();

            const xhr = new XMLHttpRequest();

            // 업로드 진행률 업데이트
            xhr.upload.onprogress = (event) => {
                const percentage = parseInt((event.loaded / event.total) * 100);
                setProgress(prev => {
                    const updatedProgress = prev.map(p => p.id === idx ? { ...p, percentage: percentage } : p);
                    return updatedProgress;
                });
            };

            // 요청 상태 처리
            xhr.onreadystatechange = () => {
                if (xhr.readyState !== 4) return;
                if (xhr.status === 200) {
                    setStatus('success');
                } else {
                    setStatus('error');
                }
            };

            // 서버로 파일 전송
            xhr.open('POST', 'https://httpbin.org/post', true);
            xhr.send(formData);
        });

        const allFilesUploaded = progress.every(p => p.percentage === 100);
        if (allFilesUploaded) {
            setProgress(prev => prev.map(p => ({ ...p, percentage: 0 })));
        }
    };

    const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
        onDrop,
        multiple: true,
        // parallelUploads: 5,
        accept: {
            "video/*": [],
        }
    })

    const RootProps = {
        ...getRootProps(),
    };

    useEffect(() => () => {
        // createObjectURL 로 생성한 URL을 revokeObjectURL 로 제거 해준다
        // 메모리 누수를 방지하기 위해 생성된 URL을 DOM과 바인딩한 후에는 해제하는 것이 좋다고한다.
        files.forEach(file => URL.revokeObjectURL(file.preview));
    }, [files]);

    //현재 업로드한 이미지 파일을 취소하는 기능
    const handleCancle = () => {
        console.log('cancle')
        setFiles([])
    }

    return (
        <Modal
            className="video-upload-modal"
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            disablePortal
        >
            <div className="modal-container">
                <div className="modal-header">
                    <h1>{t('header.upload_modal.video_uploader')}</h1>
                    <IconButton className="close-button" onClick={() => setModalOpen(false)}>
                        <CloseIcon />
                    </IconButton>
                </div>
                <div className="modal-body">
                    <div className="upload-vod-container">
                        {
                            files.length > 0 ?
                                <TableContainer>
                                    <Table aria-label="customized table">
                                        <TableHead className="table-header">
                                            <TableRow className="table-header-row">
                                                <TableCell className="table-header-cell">
                                                    <Typography variant="h6">{t('header.upload_modal.file_list')}</Typography>
                                                </TableCell>
                                                <TableCell className="table-header-cell"></TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {files.map((file, idx) => (
                                                status == 'success' ?
                                                    <TableRow key={idx}>
                                                        <TableCell className="file-info-cell">
                                                            <div>
                                                                <Typography variant="body1" className="file-name">{file.name}</Typography>
                                                                <Typography variant="body2" className="file-size-text">({formatFileSize(file.size)})</Typography>
                                                            </div>
                                                        </TableCell>
                                                        <TableCell align="right" className="upload-status-cell">
                                                            <div><Typography variant="body2">{t('header.upload_modal.upload_complete')}</Typography></div>
                                                        </TableCell>
                                                    </TableRow>
                                                    :
                                                    <TableRow key={idx}>
                                                        <TableCell className="video-preview-cell">
                                                            {status !== 'success' && videoUrls.length > idx && (
                                                                <div className="video-preview">
                                                                    <video controls>
                                                                        <source src={videoUrls[idx]} type="video/mp4" />
                                                                        Your browser does not support the video tag.
                                                                    </video>
                                                                </div>
                                                            )}
                                                        </TableCell>
                                                        <TableCell className="file-info-cell">
                                                            <div>
                                                                <Typography variant="body1" className="file-name">{file.name}</Typography>
                                                                <Typography variant="body2">({formatFileSize(file.size)})</Typography>
                                                            </div>
                                                            {
                                                                progress.length > 0 &&
                                                                <Box className="progress-container">
                                                                    {
                                                                        status != 'success' &&
                                                                        <ProgressWithLabel value={file.id == progress[idx].id ? progress[idx].percentage : 0} />
                                                                    }
                                                                </Box>
                                                            }
                                                        </TableCell>
                                                    </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                :
                                <div className="dropzone-area" {...RootProps}>
                                    {isDragReject ? (
                                        <Typography className="drag-reject-text">{t('header.upload_modal.file_format_error')}</Typography>
                                    ) : isDragActive ? (
                                        <div className="drag-active-content">
                                            <Typography>{t('header.upload_modal.drag_and_drop')}</Typography>
                                        </div>
                                    ) : (
                                        <div className="dropzone-content">
                                            <Typography>{t('header.upload_modal.drag_and_drop')}</Typography>
                                        </div>
                                    )}
                                </div>
                        }
                    </div>

                    <div className="upload-status">
                        <Button
                            className="upload-button"
                            variant="contained"
                            color="primary"
                            onClick={onUpload}
                        >
                            <CloudUploadIcon className="upload-icon" />
                            {t('header.upload_modal.upload')}
                        </Button>
                        <div>
                            <Typography>
                                {t('header.upload_modal.total_size', {
                                    size: formatFileSize(files.reduce((total, file) => total + file.size, 0)),
                                    count: files.length
                                })}
                            </Typography>
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    );
}

export default VodUploadModal;
